import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, CardHeader } from '../components/ui';
import { showToast } from '../utils/toast';
import { reconciliationService, ReconciliationRunDetail, ReconciliationRunEntityStat } from '../services/reconciliation.service';

function statusClass(status: string): string {
  if (status === 'PASS' || status === 'COMPLETED') return 'bg-green-100 text-green-800';
  if (status === 'FAILED') return 'bg-red-100 text-red-800';
  if (status === 'RUNNING' || status === 'HEALING') return 'bg-blue-100 text-blue-800';
  if (status === 'MISMATCH') return 'bg-orange-100 text-orange-800';
  if (status === 'SKIPPED') return 'bg-gray-100 text-gray-700';
  return 'bg-yellow-100 text-yellow-800';
}

function EntityRow({
  entity,
  onSelect,
  onRetry,
  retrying,
}: {
  entity: ReconciliationRunEntityStat;
  onSelect: () => void;
  onRetry: (entityKey: string) => void;
  retrying: boolean;
}) {
  const metrics = entity.metrics;

  return (
    <tr className="border-b border-xtnd-light/50 dark:border-xtnd-dark-700/40">
      <td className="py-2">
        <button className="text-xtnd-blue hover:underline" onClick={onSelect}>
          {entity.entityKey}
        </button>
      </td>
      <td className="py-2">{metrics.clioCount}</td>
      <td className="py-2">{metrics.redshiftCount}</td>
      <td className="py-2">{metrics.countDelta}</td>
      <td className="py-2">{metrics.updatedAtLagOverThreshold}</td>
      <td className="py-2">{metrics.updatedAtLagMaxMinutes}</td>
      <td className="py-2">{metrics.updatedAtLagP95Minutes}</td>
      <td className="py-2">{metrics.updatedAtLagAvgMinutes}</td>
      <td className="py-2">{metrics.healRequested}</td>
      <td className="py-2">{metrics.healFailed}</td>
      <td className="py-2">{String(metrics.resyncRequested)}</td>
      <td className="py-2">
        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${statusClass(entity.resultStatus || entity.taskStatus)}`}>
          {entity.resultStatus || entity.taskStatus}
        </span>
      </td>
      <td className="py-2">
        <Button size="sm" variant="outline" onClick={() => onRetry(entity.entityKey)} loading={retrying}>
          Retry
        </Button>
      </td>
    </tr>
  );
}

export function ReconciliationRunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const queryClient = useQueryClient();

  const [selectedEntity, setSelectedEntity] = React.useState<{ entityKey: string; tenantId: string } | null>(null);

  const runQuery = useQuery<ReconciliationRunDetail>({
    queryKey: ['reconciliation-run', runId],
    queryFn: () => reconciliationService.getRun(runId!),
    enabled: Boolean(runId),
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) return 5000;
      return data.status === 'RUNNING' || data.status === 'CANCEL_REQUESTED' ? 4000 : 12000;
    },
  });

  const entityArtifactsQuery = useQuery({
    queryKey: ['reconciliation-entity-artifacts', runId, selectedEntity?.entityKey, selectedEntity?.tenantId],
    queryFn: () =>
      reconciliationService.getEntityArtifacts(runId!, selectedEntity!.entityKey, selectedEntity!.tenantId),
    enabled: Boolean(runId && selectedEntity),
  });

  const cancelMutation = useMutation({
    mutationFn: () => reconciliationService.cancelRun(runId!),
    onSuccess: () => {
      showToast.success('Run cancellation requested');
      queryClient.invalidateQueries({ queryKey: ['reconciliation-run', runId] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation-runs'] });
    },
    onError: (error: Error) => showToast.error(error.message || 'Failed to cancel run'),
  });

  const resumeMutation = useMutation({
    mutationFn: () => reconciliationService.resumeRun(runId!),
    onSuccess: () => {
      showToast.success('Run resumed');
      queryClient.invalidateQueries({ queryKey: ['reconciliation-run', runId] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation-runs'] });
    },
    onError: (error: Error) => showToast.error(error.message || 'Failed to resume run'),
  });

  const retryMutation = useMutation({
    mutationFn: (entityKey: string) => reconciliationService.retryEntity(runId!, entityKey),
    onSuccess: result => {
      showToast.success(`Retry requested for ${result.entityKey}`);
      queryClient.invalidateQueries({ queryKey: ['reconciliation-run', runId] });
      if (selectedEntity) {
        queryClient.invalidateQueries({
          queryKey: ['reconciliation-entity-artifacts', runId, selectedEntity.entityKey, selectedEntity.tenantId],
        });
      }
    },
    onError: (error: Error) => showToast.error(error.message || 'Failed to retry entity'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white">Reconciliation Run Detail</h1>
          <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light mt-1">Run ID: {runId}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/reconciliation">
            <Button variant="outline">Back</Button>
          </Link>
          <Button variant="destructive" onClick={() => cancelMutation.mutate()} loading={cancelMutation.isPending}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => resumeMutation.mutate()} loading={resumeMutation.isPending}>
            Resume
          </Button>
        </div>
      </div>

      {runQuery.isLoading && <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light">Loading run...</div>}
      {runQuery.isError && <div className="text-sm text-red-600">Failed to load run: {(runQuery.error as Error).message}</div>}

      {runQuery.data && (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">Run Summary</h2>
                <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${statusClass(runQuery.data.status)}`}>
                  {runQuery.data.status}
                </span>
              </div>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-xtnd-dark dark:text-xtnd-light">
                <div>Tenants: {runQuery.data.tenantIds.join(', ')}</div>
                <div>Dry Run: {String(runQuery.data.dryRun)}</div>
                <div>Heal Enabled: {String(runQuery.data.healEnabled)}</div>
                <div>Progress: {runQuery.data.counts.completed + runQuery.data.counts.failed + runQuery.data.counts.cancelled}/{runQuery.data.counts.total}</div>
                <div>Created: {new Date(runQuery.data.createdAt).toLocaleString()}</div>
                <div>Updated: {new Date(runQuery.data.updatedAt).toLocaleString()}</div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">Per-Entity Results</h2>
            </CardHeader>
            <CardBody>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-xtnd-light dark:border-xtnd-dark-700">
                      <th className="py-2">Entity</th>
                      <th className="py-2">Clio</th>
                      <th className="py-2">Redshift</th>
                      <th className="py-2">Delta</th>
                      <th className="py-2">Lag &gt; Threshold</th>
                      <th className="py-2">Max Lag</th>
                      <th className="py-2">P95 Lag</th>
                      <th className="py-2">Avg Lag</th>
                      <th className="py-2">Heal Requested</th>
                      <th className="py-2">Heal Failed</th>
                      <th className="py-2">Resync Requested</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runQuery.data.entityStats.map(entity => (
                      <EntityRow
                        key={`${entity.tenantId}-${entity.entityKey}`}
                        entity={entity}
                        onSelect={() => setSelectedEntity({ entityKey: entity.entityKey, tenantId: entity.tenantId })}
                        onRetry={entityKey => retryMutation.mutate(entityKey)}
                        retrying={retryMutation.isPending && retryMutation.variables === entity.entityKey}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>

          {selectedEntity && (
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                  Entity Drill-down: {selectedEntity.entityKey} ({selectedEntity.tenantId})
                </h2>
              </CardHeader>
              <CardBody>
                {entityArtifactsQuery.isLoading && <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light">Loading entity artifacts...</div>}
                {entityArtifactsQuery.isError && (
                  <div className="text-sm text-red-600">
                    Failed to load artifacts: {(entityArtifactsQuery.error as Error).message}
                  </div>
                )}
                {entityArtifactsQuery.data && (
                  <div className="space-y-4 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>Missing in Redshift: {entityArtifactsQuery.data.missingInRedshift.length}</div>
                      <div>Extra in Redshift: {entityArtifactsQuery.data.extraInRedshift.length}</div>
                      <div>Top stale IDs: {entityArtifactsQuery.data.topStaleIds.length}</div>
                    </div>

                    <div>
                      <h3 className="font-medium mb-2 text-xtnd-dark dark:text-xtnd-white">Top Stale IDs</h3>
                      <div className="max-h-48 overflow-y-auto border border-xtnd-light dark:border-xtnd-dark-700 rounded p-2">
                        {entityArtifactsQuery.data.topStaleIds.length === 0 && <div>None</div>}
                        {entityArtifactsQuery.data.topStaleIds.map(item => (
                          <div key={item.id} className="py-1 border-b border-xtnd-light/40 dark:border-xtnd-dark-700/40 last:border-0">
                            {item.id} ({item.lagMinutes} minutes)
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-medium mb-2 text-xtnd-dark dark:text-xtnd-white">Heal Actions</h3>
                      <div className="max-h-48 overflow-y-auto border border-xtnd-light dark:border-xtnd-dark-700 rounded p-2">
                        {entityArtifactsQuery.data.healActions.length === 0 && <div>None</div>}
                        {entityArtifactsQuery.data.healActions.map((action, index) => (
                          <div key={`${action.entityId}-${action.action}-${index}`} className="py-1 border-b border-xtnd-light/40 dark:border-xtnd-dark-700/40 last:border-0">
                            {action.action} {action.entityId} requested={String(action.requested)} mechanism={action.mechanism}
                            {action.error ? ` error=${action.error}` : ''}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default ReconciliationRunDetailPage;
